<template>
  <div>
    <DpplTreeselect
      v-model="value"
      :async="async"
      :options="validUsersConverted"
      :async-url="{ path: '/vue/lookup-share-user', params: asyncParams }"
      :placeholder="loading ? 'Loading...' : 'Select...'"
      label="Select User(s)"
      response-value-key="users"
      label-key="email"
      :search-keys="['name', 'email']"
      show-select-all
      :max-height="200"
      :hide-options="maxUsers === value.length"
      :disabled="disabled || loading"
      @update:model-value="handleSelectUser"
      @add-snackbar="addErrorSnackbar"
    >
      <template #option-label="{ option }">
        <div class="d-flex flex-column align-start justify-start">
          <p class="treeselect-name">
            {{ option.name }}
          </p>
          <p class="treeselect-email">
            {{ option.email }}
          </p>
        </div>
      </template>
    </DpplTreeselect>
    <v-input :error-messages="localError ? [localError] : []" :class="{ 'mb-2': localError }" />
  </div>
</template>

<script setup>
import {
  ref, computed, onMounted,
} from 'vue';
import { getAsyncSearchResults } from '@/api/asyncSearch';
import { useAppSnackbar } from '@/stores/app/snackbar';
import DpplTreeselect from '@/components/datapeople/DpplTreeselect.vue';

const props = defineProps({
  disabled: { type: Boolean, default: false },
  asyncParams: { type: Object, default: () => ({}) },
  maxUsers: { type: Number, default: 20 },
});

const emit = defineEmits(['select-user']);

const { addErrorSnackbar } = useAppSnackbar();

const value = ref([]);
const validUsers = ref([]);
const async = ref(false);
const loading = ref(false);
const localError = ref('');

const validUsersConverted = computed(() => validUsers.value
  .filter(({ email }) => !email.endsWith('.domain.tld'))
  .map((user) => ({
    id: user.id,
    label: user.email,
    name: user.name ? user.name : `${user.first_name} ${user.last_name}`,
    email: user.email,
  })));

const handleSelectUser = ($event) => {
  const selected = Array.isArray($event) ? $event : [];
  if (selected.length === props.maxUsers) {
    localError.value = `You have selected the maximum of ${props.maxUsers} users. Grant access to these users before adding more.`;
  } else {
    localError.value = '';
  }

  let userIds = $event;
  let userEmails = $event;

  if (Array.isArray($event)) {
    userIds = $event.map(({ id }) => id);
    userEmails = $event.map(({ email }) => email);
  }

  emit('select-user', { userIds, userEmails });
};

onMounted(async () => {
  try {
    loading.value = true;
    const { attribute_options: attrOptions } = await getAsyncSearchResults({
      urlObj: { path: '/settings-api/demographic-survey-grant-access', params: props.asyncParams },
    });

    const { async: isAsync, options: users = [] } = attrOptions.userIds;

    if (isAsync) async.value = isAsync;
    if (users) validUsers.value = users;
  } catch (error) {
    addErrorSnackbar({
      text: 'Unable to fetch user list',
    });
    throw error;
  } finally {
    loading.value = false;
  }
});
</script>

<style lang="less" scoped>
.treeselect-name {
  font-weight: 400;
}
.treeselect-email {
  font-weight: 300;
  padding-top: 2px;
}
:deep(.vue-treeselect__placeholder) {
  color: @text-lighter !important;
}
:deep(.v-input--is-disabled .v-input__slot) {
  background: @bg-canvas !important;
}
</style>
